/** Exception issue types (must match elementWageTypeEngine categories). */
export const EXCEPTION_CATEGORIES = [
  { value: '', label: 'All issues' },
  { value: 'Amount mismatch', label: 'Amount mismatch ($)' },
  { value: 'Missing in ADP', label: 'Missing in ADP ($)' },
  { value: 'Missing in Oracle', label: 'Missing in Oracle ($)' },
  { value: 'Unmapped Oracle element', label: 'Unmapped Oracle element' },
  { value: 'Hours mismatch', label: 'Hours mismatch' },
  { value: 'Missing in ADP (hours)', label: 'Missing in ADP (hours)' },
  { value: 'Missing in G2', label: 'Missing in G2' },
];

export const METRIC_TYPE_OPTIONS = [
  { value: '', label: 'All metrics' },
  { value: 'dollars', label: 'Dollars' },
  { value: 'hours', label: 'Hours' },
];

export const EMPTY_EXCEPTION_FILTERS = {
  q: '',
  person: '',
  category: '',
  metric: '',
  element: '',
  wageType: '',
  oracleAmount: '',
  adpAmount: '',
  difference: '',
};

function matchText(haystack, needle) {
  if (!needle || !String(needle).trim()) return true;
  return String(haystack ?? '')
    .toLowerCase()
    .includes(String(needle).trim().toLowerCase());
}

function matchPerson(personNumber, personFilter) {
  if (!personFilter || !String(personFilter).trim()) return true;
  return String(personNumber || '').includes(String(personFilter).trim());
}

function matchAmount(amount, filter) {
  if (!filter || !String(filter).trim()) return true;
  const f = String(filter).trim();
  const n = Number(amount);
  if (!Number.isFinite(n)) return false;

  if (f.startsWith('>=')) {
    const v = Number(f.slice(2).replace(/,/g, ''));
    return Number.isFinite(v) && n >= v;
  }
  if (f.startsWith('<=')) {
    const v = Number(f.slice(2).replace(/,/g, ''));
    return Number.isFinite(v) && n <= v;
  }
  if (f.startsWith('>')) {
    const v = Number(f.slice(1).replace(/,/g, ''));
    return Number.isFinite(v) && n > v;
  }
  if (f.startsWith('<')) {
    const v = Number(f.slice(1).replace(/,/g, ''));
    return Number.isFinite(v) && n < v;
  }

  const fn = Number(f.replace(/,/g, ''));
  if (!Number.isFinite(fn)) return false;
  return Math.abs(n - fn) < 0.005;
}

/** Client-side filter (mirrors services/recon/exceptionFilters.js). */
export function filterExceptionRows(list, filters = {}) {
  const {
    q = '',
    category = '',
    metric = '',
    metricType = '',
    element = '',
    person = '',
    wageType = '',
    oracleAmount = '',
    adpAmount = '',
    difference = '',
  } = filters;

  let rows = list.filter((ex) => ex.oracleElementName && !ex.classificationName);
  if (!rows.length) rows = list.filter((ex) => ex.oracleElementName || ex.adpWageTypeCodes);

  const metricFilter = metric || metricType;
  if (metricFilter) rows = rows.filter((ex) => (ex.metricType || 'dollars') === metricFilter);
  if (category) rows = rows.filter((ex) => ex.category === category);
  if (element) rows = rows.filter((ex) => matchText(ex.oracleElementName, element));
  if (person) rows = rows.filter((ex) => matchPerson(ex.personNumber, person));
  if (wageType) {
    rows = rows.filter((ex) => matchText(ex.adpWageTypeCodes || ex.adpWageTypes, wageType));
  }
  if (oracleAmount) {
    rows = rows.filter((ex) =>
      ex.metricType === 'hours'
        ? matchAmount(ex.g2Hours, oracleAmount)
        : matchAmount(ex.oracleAmount, oracleAmount)
    );
  }
  if (adpAmount) {
    rows = rows.filter((ex) =>
      ex.metricType === 'hours'
        ? matchAmount(ex.registerHours, adpAmount)
        : matchAmount(ex.adpAmount, adpAmount)
    );
  }
  if (difference) rows = rows.filter((ex) => matchAmount(ex.difference, difference));

  if (q) {
    const needle = String(q).trim().toLowerCase();
    rows = rows.filter(
      (ex) =>
        matchPerson(ex.personNumber, needle) ||
        matchText(ex.oracleElementName, needle) ||
        matchText(ex.adpWageTypeCodes || ex.adpWageTypes, needle) ||
        matchText(ex.category, needle) ||
        matchText(ex.metricType, needle)
    );
  }

  return rows;
}

export function hasActiveExceptionFilters(filters = {}) {
  return Object.values(filters).some((v) => v != null && String(v).trim() !== '');
}

export function filtersToSearchParams(filters, { limit, offset } = {}) {
  const params = new URLSearchParams();
  if (limit != null) params.set('limit', String(limit));
  if (offset != null) params.set('offset', String(offset));
  for (const [key, value] of Object.entries(filters)) {
    if (value != null && String(value).trim() !== '') params.set(key, String(value).trim());
  }
  return params;
}
